import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import ClipLoader from "react-spinners/ClipLoader";
import useAuthStore from "../store/useAuthStore";
import { toast } from "react-toastify";
import { getTemplate } from "../services/TemplateServices";
import { ReactComponent as GalleryIcon } from "../assets/gallery-send-svgrepo-com.svg";
import { ReactComponent as Camera } from "../assets/photo-camera-photocamera-svgrepo-com.svg";
import { addTag, getTags } from "../services/TagServices";
import { cardUpload, upload } from "../services/uploadServices";
// import { countryCodes } from "../utils/countryCode";

const PhotoUploadPage = () => {
  const token = useAuthStore((state) => state.token);
  const [frontFile, setFrontFile] = useState(null);
  const [backFile, setBackFile] = useState(null); // For optional back image
  const [frontPreview, setFrontPreview] = useState(null);
  const [backPreview, setBackPreview] = useState(null); // Preview for back image
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [visitingCard, setVisitingCard] = useState(null);
  const [templateList, setTemplateList] = useState([])
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [tagsList, setTagsList] = useState(null)
  const navigate = useNavigate();
  const [userFile, setUserFile] = useState(null)
  const [userPreview, setUserPreview] = useState(null);

  // const [countryCodeSelected, setCountryCodeSelected] = useState(null);

  // const countryCodeList = countryCodes.map(c => ({
  //   value: c.code,
  //   label: `${c.code} (${c.country})`,
  // }))

  // console.log(countryCodeSelected)


  useEffect(() => {
    if (!token) {
      navigate("/");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Function to resize the image
  const resizeImage = (file, maxWidth, maxHeight) => {
    return new Promise((resolve, reject) => {
      const img = document.createElement("img");
      const canvas = document.createElement("canvas");
      const reader = new FileReader();

      reader.onload = (e) => {
        img.src = e.target.result;
        img.onload = () => {
          let width = img.width;
          let height = img.height;

          if (width > maxWidth || height > maxHeight) {
            if (width > height) {
              height *= maxWidth / width;
              width = maxWidth;
            } else {
              width *= maxHeight / height;
              height = maxHeight;
            }
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, width, height);

          canvas.toBlob(
            (blob) => {
              resolve(
                new File([blob], file.name, {
                  type: file.type,
                  lastModified: Date.now(),
                })
              );
            },
            file.type,
            1
          );
        };
      };

      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  };

  const handleFrontFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      try {
        const resizedFile = await resizeImage(selectedFile, 1280, 720);
        setFrontFile(resizedFile);
        const previewUrl = URL.createObjectURL(resizedFile);
        setFrontPreview(previewUrl);
      } catch (error) {
        console.error("Error resizing image:", error);
      }
    }
  };

  const handleBackFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      try {
        const resizedFile = await resizeImage(selectedFile, 1280, 720);
        setBackFile(resizedFile);
        const previewUrl = URL.createObjectURL(resizedFile);
        setBackPreview(previewUrl);
      } catch (error) {
        console.error("Error resizing image:", error);
      }
    }
  };

  // Handle photo upload (front required, back optional)
  const handlePhotoUpload = async () => {
    if (!frontFile) {
      alert("Please select the front image first.");
      return;
    }

    const formData = new FormData();
    formData.append("front", frontFile);
    if (backFile) {
      formData.append("back", backFile); // Append back image if provided
    }

    try {
      setLoading(true);
      // const res = await fetch(
      //   process.env.REACT_APP_BACKEND_URL + "/api/card/upload",
      //   {
      //     method: "POST",
      //     headers: {
      //       Authorization: `Bearer ${token}`,
      //     },
      //     body: formData,
      //   }
      // );

      const res = await cardUpload(formData);

      const data = await res.data;

      if (res.status === 200) {
        alert("Photo uploaded successfully");
        setVisitingCard(data.visitingCard);
      } else {
        alert(data.message);
      }
    } catch (error) {
      console.error("Error during photo upload:", error);
      alert("Photo upload failed, please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!visitingCard) return;

    try {
      setUpdating(true);

      // const payload = {
      //   ...visitingCard,
      //   templateId: selectedTemplateId,
      //   userImage:userFile
      //   // countryCode:countryCodeSelected?.value
      // };

      const formData = new FormData();
      formData.append("templateId", selectedTemplateId);
      formData.append("company", visitingCard.company || "");
      formData.append("firstName", visitingCard.firstName || "");
      formData.append("lastName", visitingCard.lastName || "");
      formData.append("mobile", visitingCard.mobile || "");
      formData.append("email", visitingCard.email || "");
      formData.append("remark", visitingCard.remark || "");
      formData.append("tag", visitingCard.tag || "");

      if (userFile) {
        formData.append("userImage", userFile);
      }

      const res = await upload(formData, visitingCard.id)
      const data = await res.data;

      if (res.status === 200) {
        toast.success("Visiting card updated successfully 🎉");
        resetVisitingCard();
        resetImages();
        setUserPreview(null)
      } else {
        toast.error(data.message || "Update failed ❌");
      }
    } catch (error) {
      console.error("Error during visiting card update:", error);
      toast.error(error.response?.data?.message || "Failed to update visiting card, please try again ❗");
    } finally {
      setUpdating(false);
    }
  };

  // Handle visiting card field changes
  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setVisitingCard((prev) => ({ ...prev, [name]: value }));
  };

  // Reset front and back images
  const resetImages = () => {
    setFrontFile(null);
    setBackFile(null);
    setFrontPreview(null);
    setBackPreview(null);
    setUserFile(null)
  };

  const resetVisitingCard = () => {
    setVisitingCard(null);
  };

  const handleCancel = () => {
    resetImages();
    resetVisitingCard();
  };

  const allTemplates = async () => {
    const res = await getTemplate(token);
    setTemplateList(res?.data?.data || []);
  };

  const tagList = async () => {
    const res = await getTags();
    setTagsList(res?.data)

  }

  useEffect(() => {
    if (!token) {
      navigate("/");
    } else {
      allTemplates();
      tagList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (templateList.length > 0) {
      const defaultTemplate = templateList.find(t => t.isDefault);
      if (defaultTemplate) {
        setSelectedTemplateId(defaultTemplate.id);
      }
    }
  }, [templateList]);

  const handleAddTag = async () => {
    const name = prompt("Enter new tag name:");
    if (name.trim()) {
      await addTag(name);
      tagList();
    }
  };

  const handleUserImage = async (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      try {
        const resizedFile = await resizeImage(selectedFile, 1280, 720);
        setUserFile(resizedFile);

        // Optional: generate preview
        const previewUrl = URL.createObjectURL(resizedFile);
        setUserPreview(previewUrl); // use a separate state for preview
      } catch (error) {
        console.error("Error setting user image:", error);
      }
    }
  };

  return (
    <motion.div
      className="flex flex-col items-center"
      initial={{ opacity: 0, y: -50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Upload Section */}
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-md p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-700 mb-4">Upload Visiting Card</h3>

        {/* Front Image Upload */}
        {!frontPreview ? (
          <div className="flex gap-4">
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-blue-400 transition">
              <label className="flex flex-col items-center space-y-2 cursor-pointer">
                <GalleryIcon className="h-10 w-10 text-gray-400" />
                <span className="text-gray-500">Upload Front Image (Required)</span>
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleFrontFileChange}
                />
              </label>
            </div>
            <div className="flex justify-center items-center">or</div>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-blue-400 transition">
              <label className="flex flex-col items-center space-y-2 cursor-pointer">
                <Camera className="h-10 w-10 text-gray-400" />
                <span className="text-gray-500">Capture Front Image (Required)</span>
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleFrontFileChange}
                  capture="environment"
                />
              </label>
            </div>
          </div>
        ) : (
          <motion.div
            className="mb-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <p className="mb-2 text-sm font-medium text-gray-600">Front Image Preview:</p>
            <img
              src={frontPreview}
              alt="Front Preview"
              className="h-48 w-full object-cover rounded-lg shadow"
            />
          </motion.div>
        )}

        {/* Back Image Upload (Optional) */}
        {frontPreview && !backPreview && !visitingCard && (
          <>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-blue-400 transition mt-4">
              <label className="flex flex-col items-center space-y-2 cursor-pointer">
                <GalleryIcon className="h-10 w-10 text-gray-400" />
                <span className="text-gray-500">Upload Back Image (Optional)</span>
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleBackFileChange}
                />
              </label>
            </div>
            <div className="flex justify-center items-center">or</div>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-blue-400 transition mt-2">
              <label className="flex flex-col items-center space-y-2 cursor-pointer">
                <Camera className="h-10 w-10 text-gray-400" />
                <span className="text-gray-500">Capture Back Image (Optional)</span>
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleBackFileChange}
                  capture="camera"
                />
              </label>
            </div>
          </>

        )}

        {/* Back Image Preview */}
        {backPreview && (
          <motion.div
            className="mt-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <p className="mb-2 text-sm font-medium text-gray-600">Back Image Preview:</p>
            <img
              src={backPreview}
              alt="Back Preview"
              className="h-48 w-full object-cover rounded-lg shadow"
            />
          </motion.div>
        )}
      </div>


      {visitingCard && (
        <div className="flex flex-col mb-4 w-full">
          <h2 className="text-xl font-semibold mb-4">Visiting Card Details</h2>
          <label className="mb-2">Company Name:</label>
          <input
            className="p-2 border border-gray-300 rounded mb-2"
            type="text"
            name="company"
            value={visitingCard.company}
            onChange={handleFieldChange}
            placeholder="Company Name"
          />
          <label className="mb-2">First Name:</label>
          <input
            className="p-2 border border-gray-300 rounded mb-2"
            type="text"
            name="firstName"
            value={visitingCard.firstName}
            onChange={handleFieldChange}
            placeholder="First Name"
          />
          <label className="mb-2">Last Name:</label>
          <input
            className="p-2 border border-gray-300 rounded mb-2"
            type="text"
            name="lastName"
            value={visitingCard.lastName}
            onChange={handleFieldChange}
            placeholder="Last Name"
          />
          {/* <label className="mb-2">Mobile:</label>
          <input
            className="p-2 border border-gray-300 rounded mb-2"
            type="text"
            name="mobile"
            value={visitingCard.mobile}
            onChange={handleFieldChange}
            placeholder="Mobile"
            maxLength={10}
          /> */}

          {/* <label className="mb-2">Mobile:</label>
          <div className="flex">
            <Select
              options={countryCodeList}
              value={countryCodeSelected}
              onChange={(code) => setCountryCodeSelected(code)}
              placeholder="Code"
              isSearchable
              className="w-5/12 h-10"
            />
            <input
              className="p-2 border border-gray-300 rounded-sm flex-1 w-7/12 h-10"
              type="text"
              name="mobile"
              value={visitingCard.mobile}
              onChange={handleFieldChange}
              placeholder="Enter number"
              maxLength={10}
            />
          </div>  */}

          <label className="mb-2">Mobile:</label>
          <input
            className="p-2 border border-gray-300 rounded mb-2"
            type="text"
            name="mobile"
            value={visitingCard.mobile}
            onChange={handleFieldChange}
            placeholder="Enter mobile with Country Code"
          />


          <label className="mb-2">Email:</label>
          <input
            className="p-2 border border-gray-300 rounded mb-2"
            type="text"
            name="email"
            value={visitingCard.email}
            onChange={handleFieldChange}
            placeholder="Email"
          />
          {/* <div className="flex flex-col mb-4 w-full"> */}
          <label className="mb-2 font-medium">Select Mail Template:</label>
          <select
            className="p-2 border border-gray-300 rounded mb-4"
            value={selectedTemplateId}
            onChange={(e) => setSelectedTemplateId(e.target.value)}
          >
            <option value="">Select Template</option>
            {templateList?.map((template) => (
              <option key={template.id} value={template.id}>
                {template.name}
              </option>
            ))}
          </select>
          <label className="mb-2">Remark:</label>
          <input
            className="p-2 border border-gray-300 rounded mb-2"
            type="text"
            name="remark"
            value={visitingCard.remark}
            onChange={handleFieldChange}
            placeholder="Remark"
          />
          <label className="mb-0">Tag:</label>
          <div className="flex items-center space-x-2 mb-4">
            <select
              name="tag"
              className="p-2 border border-gray-300 rounded w-full"
              onChange={handleFieldChange}
            >
              <option value="">Select Option</option>
              {tagsList?.map((tag, index) => (
                <option key={index} value={tag.name}>
                  {tag.name}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={handleAddTag}
              className="px-3 py-1 bg-blue-500 text-white rounded"
            >
              +
            </button>
          </div>
          <div className="flex flex-col items-center space-y-2 mb-2">

            {/* Take Photo Button */}
            <label
              htmlFor="captureImage"
              className="cursor-pointer bg-blue-500 text-white px-4 py-2 rounded-md shadow-md hover:bg-blue-600 transition"
            >
              📸 Take Visitor Photo
            </label>
            <input
              id="captureImage"
              type="file"
              name="userImage"
              accept="image/*"
              capture="environment"   // only camera
              className="hidden"
              onChange={handleUserImage}
            />

            {/* Upload Photo Button */}
            <label
              htmlFor="uploadImage"
              className="cursor-pointer bg-green-500 text-white px-4 py-2 rounded-md shadow-md hover:bg-green-600 transition"
            >
              📁 Upload Visitor Photo
            </label>
            <input
              id="uploadImage"
              type="file"
              accept="image/*"        // file picker only (no capture attribute)
              className="hidden"
              onChange={handleUserImage}
            />

            {/* Preview */}
            {userPreview && (
              <img
                src={userPreview}
                alt="User"
                className="h-48 w-full object-cover rounded-lg shadow"
              />
            )}
          </div>

          {/* </div> */}
          <button
            className="bg-blue-500 text-white px-4 py-2 rounded"
            onClick={handleUpdate}
            disabled={updating}
          >
            {updating ? (
              <ClipLoader size={25} color="#ffffff" />
            ) : (
              "Update Details"
            )}
          </button>

          <button
            className="bg-red-500 text-white px-4 py-2 rounded mt-2"
            onClick={handleCancel}
          >
            Cancel
          </button>
        </div>
      )}

      {frontPreview && !visitingCard && (
        <motion.button
          className="bg-green-500 text-white px-4 py-2 rounded"
          onClick={handlePhotoUpload}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          disabled={loading}
        >
          {loading ? <ClipLoader size={25} color="#ffffff" /> : "Upload Photo"}
        </motion.button>
      )}
    </motion.div>
  );
};

export default PhotoUploadPage;
